import './styles/main.css';

import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import useTreeChanges from 'tree-changes-hook';

import { useAppSelector } from '~/modules/hooks';
import theme from '~/modules/theme';

import { alertShow } from '~/actions';

import Footer from '~/components/Footer';
import Header from '~/components/Header';
import SystemAlerts from '~/containers/SystemAlerts';
import NotFound from '~/routes/NotFound';
import { trackPageView } from '~/utils/analytics';

import { selectUser } from '~/selectors';

import Home from './routes/Home';

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
`;

const Main = styled.main`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding-top: 64px;
`;

/* ─── GLOBAL SEO DEFAULTS ──────────────────────────────────────────────── */
const OG_IMAGE = '/media/images/og-image-v1.png';
const SITE_URL = 'https://cryotype.app';
const SITE_NAME = 'Cryotype';
const DEFAULT_TITLE = 'Cryotype — Free Online Typing Test';
const DEFAULT_DESC =
  'A free precision online typing test. Measure WPM, raw WPM, and accuracy with real-time feedback, three difficulty levels, history tracking, and shareable results.';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESC,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/') {
      return;
    }

    const path = `${location.pathname}${location.search}${location.hash}`;

    trackPageView(path, document.title);
  }, [location.hash, location.pathname, location.search]);

  return null;
}

function Root() {
  const dispatch = useDispatch();
  const user = useAppSelector(selectUser);
  const { changed } = useTreeChanges(user);

  useEffect(() => {
    if (changed('isAuthenticated', true)) {
      dispatch(alertShow('Welcome!', { type: 'success', icon: 'bell', timeout: 5 }));
    }
  }, [dispatch, changed]);

  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <AppWrapper data-component-name="app">
          <AnalyticsTracker />

          {/* ── GLOBAL SEO HEAD ────────────────────────────────────────── */}
          <Helmet
            defaultTitle={DEFAULT_TITLE}
            defer={false}
            encodeSpecialCharacters
            htmlAttributes={{ lang: 'en' }}
            titleTemplate={`%s | ${SITE_NAME}`}
          >
            {/* Core */}
            <meta name="description" content={DEFAULT_DESC} />
            <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
            <meta name="author" content="Cryotype" />
            <meta name="theme-color" content="#0a0d12" />
            <meta name="color-scheme" content="dark" />
            <link rel="canonical" href={SITE_URL} />

            {/* Open Graph */}
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={DEFAULT_TITLE} />
            <meta property="og:description" content={DEFAULT_DESC} />
            <meta property="og:url" content={SITE_URL} />
            <meta property="og:image" content={`${SITE_URL}${OG_IMAGE}`} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content="Cryotype — Free Online Typing Test" />
            <meta property="og:locale" content="en_US" />

            {/* Twitter / X Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={DEFAULT_TITLE} />
            <meta name="twitter:description" content={DEFAULT_DESC} />
            <meta name="twitter:image" content={`${SITE_URL}${OG_IMAGE}`} />
            <meta name="twitter:image:alt" content="Cryotype — Free Online Typing Test" />

            {/* PWA / App */}
            <meta name="application-name" content={SITE_NAME} />
            <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
            <meta name="apple-mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
            <meta name="mobile-web-app-capable" content="yes" />

            {/* Structured data — WebSite */}
            <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
          </Helmet>

          <Header />
          <Main>
            <Routes>
              <Route element={<Home />} path="/home" />
              <Route element={<Navigate to="/home" />} path="/" />
              <Route element={<NotFound />} path="*" />
            </Routes>
          </Main>
          <Footer />
          <SystemAlerts />
        </AppWrapper>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default Root;
